import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell, FaMoneyBillWave } from "react-icons/fa";
import moment from "moment";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetTreatmentComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petTreatments, setPetTreatments] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingAppointmentId, setEditingAppointmentId] = useState(null);
    const [selectedTreatments, setSelectedTreatments] = useState([]);

    const getTodayDateTimeLocal = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const local = new Date(now.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    const [treatmentDate, setTreatmentDate] = useState(getTodayDateTimeLocal());
    const [generalNote, setGeneralNote] = useState("");
    const [openAccordion, setOpenAccordion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasTreatmentForAppointment, setHasTreatmentForAppointment] = useState(false);

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return "0";
        const num = parseFloat(price);
        if (isNaN(num)) return "0";
        return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    const sumPrices = (items) => items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    useEffect(() => {
        fetchPetTreatments();
    }, [appointmentId, petId]);

    const fetchPetTreatments = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-treatment/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const treatmentsForPet = response.data.pet_treatments || [];
                setPetTreatments(treatmentsForPet);
                setTreatments(response.data.treatments || []);
                setHasTreatmentForAppointment(
                    treatmentsForPet.some(item =>
                        String(item.appointment_id) === String(appointmentId) ||
                        String(item.appointment?.id) === String(appointmentId)
                    )
                );
            }
        } catch (error) {
            console.error("Error fetching pet treatments:", error);
            showErrorToast(t('failed to load treatments'));
            setPetTreatments([]);
            setTreatments([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTreatmentsSelect = (selected) => {
        const list = selected || [];
        setSelectedTreatments(
            list.map(s => {
                const existing = selectedTreatments.find(x => x.value === s.value);
                if (existing) return existing;
                const treatmentItem = treatments.find(item => item.id === s.value);
                return { value: s.value, label: s.label, price: treatmentItem?.price || 0 };
            })
        );
    };

    const handleTreatmentPriceChange = (value, price) => {
        setSelectedTreatments(prev => prev.map(t => t.value === value ? { ...t, price } : t));
    };

    const handleAddTreatment = () => {
        setEditingAppointmentId(null);
        setSelectedTreatments([]);
        setTreatmentDate(getTodayDateTimeLocal());
        setGeneralNote("");
        setShowModal(true);
    };

    const handleEditTreatment = (apptId, group) => {
        setEditingAppointmentId(apptId);
        setSelectedTreatments(
            group.map(pt => ({
                value: pt.treatment_id,
                label: pt.treatment?.name || 'N/A',
                price: pt.price || 0,
            }))
        );
        setTreatmentDate(group[0]?.date ? group[0].date.substring(0, 16) : getTodayDateTimeLocal());

        const firstNote = group.find(pt => pt.note && (Array.isArray(pt.note) ? pt.note.length > 0 : pt.note.note));
        const noteText = firstNote
            ? (Array.isArray(firstNote.note) ? firstNote.note[0].note : firstNote.note.note)
            : "";
        setGeneralNote(noteText || "");

        setShowModal(true);
    };

    const handleDeleteClick = (apptId) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const AuthToken = localStorage.getItem("authToken");
                    const response = await axios.delete(
                        `${process.env.REACT_APP_API_URL}/pet-treatment-delete/${apptId}`,
                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                    );

                    if (response.data) {
                        fetchPetTreatments();
                        showSuccessToast(t('Treatment deleted successfully!'));
                        return true;
                    }
                } catch (error) {
                    console.error("Error deleting treatment:", error);
                    showErrorToast(t('failed to delete treatment'));
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedTreatments.length === 0) {
            showErrorToast(t('please select treatment'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingAppointmentId
                ? `${process.env.REACT_APP_API_URL}/pet-treatment-update/${editingAppointmentId}`
                : `${process.env.REACT_APP_API_URL}/pet-treatment`;

            const payload = {
                appointment_id: appointmentId,
                date: treatmentDate || null,
                note: generalNote?.trim() || null,
                treatments: selectedTreatments.map(t => ({ treatment_id: t.value, price: t.price || null })),
            };

            const response = await axios.post(url, payload, { headers: { Authorization: `Bearer ${AuthToken}` } });

            if (response.data) {
                setShowModal(false);
                showSuccessToast(editingAppointmentId ? t('Treatment updated successfully!') : t('Treatment added successfully!'));
                fetchPetTreatments();
            }
        } catch (error) {
            console.error("Error saving treatment:", error);
            showErrorToast(error.response?.data?.message || t('failed to save treatment'));
        } finally {
            setSubmitting(false);
        }
    };

    const getTreatmentOptions = () => treatments.map(item => ({ value: item.id, label: item.name, price: item.price }));

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#e0e0e0' : 'white',
            color: '#333',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': { borderColor: '#86b7fe' }
        })
    };

    const groupTreatmentsByAppointment = () => {
        const grouped = {};
        petTreatments.forEach(pt => {
            const apptId = pt.appointment_id;
            if (!grouped[apptId]) {
                grouped[apptId] = { appointment: pt.appointment, treatments: [] };
            }
            grouped[apptId].treatments.push(pt);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading treatments')}</p>
            </div>
        );
    }

    const treatmentOptions = getTreatmentOptions();

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('treatments list')}</h6>
                {isAppointmentOpen && !hasTreatmentForAppointment && (
                    <Button variant="primary" size="sm" onClick={handleAddTreatment} className="d-flex align-items-center gap-2">
                        <FaPlus size={12} /> {t('add treatment')}
                    </Button>
                )}
            </div>

            {!petTreatments || petTreatments.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaStethoscope size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no treatments found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupTreatmentsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, treatments: appointmentTreatments } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentTreatments.length} {t('treatment(s)')}
                                            </Badge>
                                            <Badge bg="success" className="px-3 py-2 d-flex align-items-center gap-1">
                                                <FaMoneyBillWave /> {formatPrice(sumPrices(appointmentTreatments))}
                                            </Badge>
                                        </div>
                                        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="primary" size="sm" onClick={() => handleEditTreatment(appointmentIdKey, appointmentTreatments)} className="d-flex align-items-center gap-1">
                                                <FaEdit size={12} />
                                                <span className="d-none d-sm-inline">{t('edit')}</span>
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDeleteClick(appointmentIdKey)} className="d-flex align-items-center gap-1">
                                                <FaTrash size={12} />
                                                <span className="d-none d-sm-inline">{t('delete')}</span>
                                            </Button>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white">
                                    <div className="p-3 bg-light rounded">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3 mb-3 pb-3 border-bottom">
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointmentTreatments[0]?.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            {appointmentTreatments[0]?.date && (
                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                    <span className="d-flex align-items-center gap-1">
                                                        <FaCalendarAlt size={14} />
                                                        {moment(appointmentTreatments[0].date).format("DD MMM YYYY")}
                                                    </span>
                                                    <span className="d-flex align-items-center gap-1">
                                                        <FaBell size={14} />
                                                        {moment(appointmentTreatments[0].date).format("hh:mm A")}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        {appointmentTreatments.map((pt, idx) => {
                                            if (!pt) return null;
                                            return (
                                                <div key={pt.id} className={idx < appointmentTreatments.length - 1 ? 'pb-3 mb-3 border-bottom' : ''}>
                                                    <Badge bg="info" className="px-3 py-2 mb-2">{pt.treatment?.name || 'N/A'}</Badge>
                                                    <div>
                                                        <strong className="text-muted">{t('Price')}:</strong>{' '}
                                                        <FaMoneyBillWave className="text-success me-1" />
                                                        {formatPrice(pt.price)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {(() => {
                                            const withNote = appointmentTreatments.find(pt => pt.note && (Array.isArray(pt.note) ? pt.note.length > 0 : pt.note.note));
                                            const noteText = withNote
                                                ? (Array.isArray(withNote.note) ? withNote.note[0].note : withNote.note.note)
                                                : null;
                                            return noteText ? (
                                                <div className="mt-3 pt-2 border-top">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <FaFileAlt className="text-success" />
                                                        <strong className="text-muted">{t('note')}:</strong>
                                                    </div>
                                                    <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{editingAppointmentId ? t('Edit Treatment') : t('Add Treatment')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Select Treatment')} <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={treatmentOptions}
                                value={selectedTreatments}
                                onChange={handleTreatmentsSelect}
                                placeholder={t('select treatment')}
                                isMulti
                                isSearchable
                                styles={customSelectStyles}
                            />
                        </Form.Group>

                        {selectedTreatments.length > 0 && (
                            <div className="mb-3">
                                <Form.Label className="fw-bold">{t('Price')}</Form.Label>
                                {selectedTreatments.map(item => (
                                    <Row key={item.value} className="align-items-center mb-2 g-2">
                                        <Col md={7} className="text-muted">{item.label}</Col>
                                        <Col md={5}>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={formatPrice(item.price)}
                                                onChange={(e) => handleTreatmentPriceChange(item.value, e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                                <div className="text-end fw-bold">
                                    {t('Total')}: {formatPrice(sumPrices(selectedTreatments))}
                                </div>
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Treatment Date')}</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={treatmentDate}
                                onChange={(e) => setTreatmentDate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Note')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                            {t('Close')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner animation="border" size="sm" /> : t('Save Changes')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}